'use client';

/** @jsxImportSource @emotion/react */
import { useState } from 'react';

import {
  Button,
  DropdownField,
  FlexItem,
  FlexLayout,
  Form,
  PhoneInputField,
  TextField,
  Typography,
  overlay,
  useTheme,
} from '@wci-tech/core-ui';

import { useMessages, useTranslations } from 'next-intl';

import { ANALYTICS_EVENTS, trackEvent } from '@/analytics';
import { useThemeMode } from '@/context';
import { submitEnquiry } from '@/services/enquiry/Enquiry.api';
import { mapEnquiryPayloadToRequestDto } from '@/services/enquiry/Enquiry.mapper';
import { HttpError } from '@/services/http/errors';
import { getResponseMessageKey } from '@/utils/getResponseMessageKey';
import { getScopedMessageKey } from '@/utils/getScopedMessageKey';

import { useStyles } from './EnquiryForm.styles';
import type { EnquiryFormValues } from './EnquiryForm.types';
import { css } from '@emotion/react';

const EnquiryForm = () => {
  const { isDark } = useThemeMode();
  const styles = useStyles(isDark);
  const { theme } = useTheme();
  const t = useTranslations('ENQUIRY_FORM');
  const messages = useMessages();
  const tAppMessages = useTranslations('APP_MESSAGES');

  const [isSubmitting, setIsSubmitting] = useState(false);

  const serviceOptions = Array.isArray(messages['ENQUIRY_FORM']?.selectServiceOptions)
    ? messages['ENQUIRY_FORM'].selectServiceOptions
    : [];

  const handleSubmitClick = () => {
    const eventData = {
      page: window.location.pathname,
      location: 'enquiry_form',
    };

    trackEvent(ANALYTICS_EVENTS.ENQUIRY_SUBMIT_CLICKED, eventData);
  };

  const handleSubmit = async (
    event: React.SyntheticEvent,
    formData: EnquiryFormValues,
    resetForm: () => void,
  ) => {
    event.preventDefault();

    if (isSubmitting) return;

    const attemptedEventData = {
      page: window.location.pathname,
      location: 'enquiry_form',
      service: formData.service,
    };

    trackEvent(ANALYTICS_EVENTS.ENQUIRY_SUBMITTED_ATTEMPTED, attemptedEventData);

    setIsSubmitting(true);

    try {
      const payload = mapEnquiryPayloadToRequestDto(formData);
      const response = await submitEnquiry(payload);

      const submittedEventData = {
        page: window.location.pathname,
        location: 'enquiry_form',
        service: formData.service,
        httpStatus: 200,
      };

      trackEvent(ANALYTICS_EVENTS.ENQUIRY_SUBMITTED, submittedEventData);

      // Get the base key name (e.g., "SUBMITTED_SUCCESSFULLY")
      const baseKey = getResponseMessageKey(response.code);

      // Dynamically construct the path "ENQUIRY.SUBMITTED_SUCCESSFULLY"
      const messagePath = getScopedMessageKey('ENQUIRY', baseKey);

      overlay.showToast(tAppMessages(messagePath), {
        type: 'success',
        theme: 'colored',
        duration: 5000,
      });

      resetForm();
    } catch (error: unknown) {
      const errorCode = error instanceof HttpError ? error.code : undefined;

      const failedEventData = {
        page: window.location.pathname,
        location: 'enquiry_form',
        service: formData.service,
        errorCode: errorCode ?? 'UNKNOWN_ERROR',
        errorType: error instanceof HttpError ? 'HttpError' : 'UnknownError',
      };

      trackEvent(ANALYTICS_EVENTS.ENQUIRY_SUBMISSION_FAILED, failedEventData);

      // Get the base key name (e.g., "SERVER_ERROR" or "TOO_MANY_REQUESTS")
      const baseKey = getResponseMessageKey(errorCode);

      const enquiryScopedKey = getScopedMessageKey('ENQUIRY', baseKey);
      const commonScopedKey = `COMMON.${baseKey}`;

      const messagePath = tAppMessages.has(enquiryScopedKey) ? enquiryScopedKey : commonScopedKey;

      overlay.showToast(tAppMessages(messagePath), {
        type: 'error',
        theme: 'colored',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Form
        onSubmit={handleSubmit}
        customStyles={styles.fieldContainer}
        autoFocusFirstField={false}
      >
        <FlexLayout padding={{ default: 0 }} direction="column" gap={32} fullWidth>
          <FlexItem>
            <Typography variant="h3" customStyles={styles.fieldtitle}>
              {t('title')}
            </Typography>
          </FlexItem>

          <FlexLayout
            padding={{ default: 0 }}
            direction="row"
            columns={12}
            gap={{ default: 0 }}
            wrap="wrap"
            fullWidth
          >
            <FlexItem columnSpan={12}>
              <TextField
                variant="secondary"
                id="name"
                appearance="filled"
                label={t('nameLabel')}
                required
                placeholder={t('namePlaceholder')}
                customStyles={{ container: styles.input }}
              />
            </FlexItem>

            <FlexItem columnSpan={12}>
              <TextField
                id="email"
                inputType="email"
                appearance="filled"
                required
                label={t('emailLabel')}
                placeholder={t('emailPlaceholder')}
                customStyles={{ container: styles.input }}
              />
            </FlexItem>

            <FlexItem columnSpan={12}>
              <PhoneInputField
                id="phoneNumber"
                appearance="filled"
                required
                label={t('phoneNumberLabel')}
                placeholder={t('phoneNumberPlaceholder')}
                customStyles={{ container: styles.input }}
              />
            </FlexItem>

            <FlexItem columnSpan={12}>
              <DropdownField
                id="service"
                appearance="filled"
                shape="sharp"
                required
                label={t('selectServiceLabel')}
                placeholder={t('selectServicePlaceholder')}
                options={serviceOptions}
                renderWithPortal
                customStyles={{
                  toggleIcon: styles.toggleIcon,
                  container: styles.input,
                  listContainer: styles.dropdown,
                  clearIcon: styles.iconColor,
                  openCloseIcon: styles.iconColor,
                  input: styles.dropdownInput,
                }}
              />
            </FlexItem>

            <FlexItem columnSpan={12}>
              <TextField
                id="message"
                appearance="filled"
                required
                inputType="text"
                label={t('messageLabel')}
                rows={8}
                multiline
                placeholder={t('messagePlaceholder')}
                customStyles={{
                  container: styles.input,
                }}
              />
            </FlexItem>

            <FlexItem columnSpan={12}>
              <Button
                type="submit"
                variant={isDark ? 'secondary' : 'primary'}
                effect="three-d"
                shape="pill"
                fullWidth
                loading={isSubmitting}
                disabled={isSubmitting}
                onClick={handleSubmitClick}
                loadingConfig={{
                  loadingIndicatorProps: {
                    spinnerType: 'mesh-gradient',
                    size: 28,
                  },
                }}
                customStyles={{
                  base: css`
                    --state-bg: ${
                      isSubmitting
                        ? isDark
                          ? theme.colors.brandColors.secondary?.light
                          : theme.colors.brandColors.primary.light
                        : isDark
                          ? theme.colors.brandColors.primary.light
                          : theme.colors.brandColors.primary.default
                    };

                    --state-border: ${
                      isSubmitting
                        ? isDark
                          ? theme.colors.brandColors.secondary?.default
                          : theme.colors.brandColors.primary.default
                        : isDark
                          ? theme.colors.brandColors.secondary?.light
                          : theme.colors.brandColors.primary.default
                    };
                  `,
                }}
              >
                {t('submitButton')}
              </Button>
            </FlexItem>
          </FlexLayout>
        </FlexLayout>
      </Form>
    </>
  );
};

export default EnquiryForm;
